import React, { useCallback, useMemo, useState } from 'react';
import BWAccordion from 'components/shared/BWAccordion';
import DataTable from 'components/shared/DataTable';
import ICON_COMMON from 'utils/icons.common';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { showConfirmModal } from 'actions/global';
import { formatPrice } from 'utils';
import { COUPON_PRODUCT_TYPE } from 'pages/Coupon/utils/constants';
import PromotionComboModal from '../modal/PromotionComboModal';

const mergeProductsFromCombos = (currentProducts = [], combos = []) => {
  const manualProducts = (Array.isArray(currentProducts) ? currentProducts : []).filter((item) => !item?.from_combo_id);
  const comboProducts = (Array.isArray(combos) ? combos : []).flatMap((combo) =>
    (Array.isArray(combo?.products) ? combo.products : []).map((product) => ({
      ...product,
      from_combo_id: combo.combo_id,
      combo_name: combo.combo_name,
    })),
  );

  const productMap = new Map();
  [...manualProducts, ...comboProducts].forEach((product) => {
    const productId = Number(product?.product_id);
    if (productId > 0) {
      productMap.set(String(productId), {
        ...product,
        product_id: productId,
      });
    }
  });
  return Array.from(productMap.values());
};

const renderComboPrice = (item) => {
  const price = Number(item?.combo_price || 0);
  if (!price) {
    return <span style={{ color: '#98a2b3', fontWeight: 700 }}>Chưa làm giá</span>;
  }
  return (
    <div>
      <b>{`${formatPrice(price, false, '.')} đ`}</b>
      {item?.combo_price_id ? (
        <p style={{ marginTop: 3, color: '#667085', fontSize: 11, fontWeight: 700 }}>
          {item?.combo_output_type_name || 'SL_PRICES'}
        </p>
      ) : null}
    </div>
  );
};

const PromotionComboApply = ({ title, disabled }) => {
  const methods = useFormContext();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const comboApplyList = methods.watch('combo_apply_list');
  const combos = useMemo(() => (Array.isArray(comboApplyList) ? comboApplyList : []), [comboApplyList]);

  const syncComboProducts = useCallback(
    (nextCombos) => {
      methods.setValue('combo_apply_list', nextCombos);
      methods.setValue('product_list', mergeProductsFromCombos(methods.watch('product_list'), nextCombos));
      methods.setValue('is_all_product', 0);
      methods.setValue('type_apply_product', COUPON_PRODUCT_TYPE.APPOINT);
      methods.setValue('check_combo_apply', nextCombos?.length ? 1 : '');
    },
    [methods],
  );

  const columns = useMemo(
    () => [
      {
        header: 'Tên combo',
        accessor: 'combo_name',
        formatter: (item) => (
          <div>
            <b>{item?.combo_name}</b>
            {item?.combo_code ? <p style={{ color: '#667085', marginTop: 4 }}>{item.combo_code}</p> : null}
          </div>
        ),
      },
      {
        header: 'Nhóm combo',
        accessor: 'combo_type_name',
      },
      {
        header: 'Số sản phẩm',
        classNameHeader: 'bw_text_center',
        classNameBody: 'bw_text_center',
        accessor: 'product_count',
      },
      {
        header: 'Giá combo',
        classNameHeader: 'bw_text_right',
        classNameBody: 'bw_text_right',
        formatter: renderComboPrice,
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      {
        globalAction: true,
        icon: ICON_COMMON.add,
        type: 'success',
        content: 'Chọn combo',
        hidden: disabled,
        onClick: () => setModalOpen(true),
      },
      {
        icon: ICON_COMMON.trash,
        color: 'red',
        hidden: disabled,
        onClick: (item) => {
          dispatch(
            showConfirmModal([`Xoá combo ${item?.combo_name || ''} khỏi chương trình khuyến mại?`], () => {
              const nextCombos = combos.filter((combo) => Number(combo?.combo_id) !== Number(item?.combo_id));
              syncComboProducts(nextCombos);
            }),
          );
        },
      },
    ],
    [combos, disabled, dispatch, syncComboProducts],
  );

  return (
    <BWAccordion title={title}>
      <DataTable
        hiddenActionRow
        noPaging
        noSelect
        plain
        columns={columns}
        data={combos}
        actions={actions}
      />
      <input
        type='hidden'
        value={methods.watch('check_combo_apply') || ''}
        {...methods.register('check_combo_apply', {
          validate: () => {
            if (!methods.watch('is_combo_promotion')) return true;
            return combos.length > 0 || 'Vui lòng chọn ít nhất một combo khuyến mại';
          },
        })}
      />
      {modalOpen && (
        <PromotionComboModal
          open={modalOpen}
          defaultDataSelect={combos}
          onClose={() => setModalOpen(false)}
          onConfirm={syncComboProducts}
        />
      )}
    </BWAccordion>
  );
};

export default PromotionComboApply;
